import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { phaseAPI } from '../services/api';
import {
  PhaseSkeleton,
  PhaseStockTable,
  PhaseSummaryCards,
  PreMarkupSection,
  StockPhaseDetailDialog,
  StockPhasesHeader,
  StockPhasesToolbar,
} from '../components/phase';

export default function StockPhases() {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [activePhase, setActivePhase] = useState('markup');
  const [stocks, setStocks] = useState([]);
  const [indices, setIndices] = useState([]);
  const [stocksLoading, setStocksLoading] = useState(false);
  const [indicesLoading, setIndicesLoading] = useState(false);

  const [minConfidence, setMinConfidence] = useState(0);
  const [showScores, setShowScores] = useState(true);
  const [showFilters, setShowFilters] = useState(false);

  const [refreshing, setRefreshing] = useState(false);

  const [searchTicker, setSearchTicker] = useState('');
  const [detailData, setDetailData] = useState(null);
  const [detailHistory, setDetailHistory] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);

  const [preMarkup, setPreMarkup] = useState(null);
  const [preMarkupLoading, setPreMarkupLoading] = useState(false);
  const [preMarkupExpanded, setPreMarkupExpanded] = useState(false);

  const openTickerDetail = useCallback(async (ticker) => {
    try {
      setDetailLoading(true);
      setDetailOpen(true);
      setDetailData(null);
      setDetailHistory(null);

      const [phaseRes, historyRes] = await Promise.all([
        phaseAPI.getPhaseLive(ticker),
        phaseAPI.getHistory(ticker, 90),
      ]);

      setDetailData(phaseRes.data);
      setDetailHistory(historyRes.data);
    } catch (err) {
      setDetailData({ error: err.response?.data?.detail || err.message });
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const fetchSummary = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const res = await phaseAPI.getSummary();
      setSummary(res.data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchStocks = useCallback(async (phase) => {
    try {
      setStocksLoading(true);
      const res = await phaseAPI.scan(phase, { minConfidence, limit: 100 });
      setStocks(res.data.stocks || []);
    } catch (err) {
      console.error('Error fetching stocks:', err);
      setStocks([]);
    } finally {
      setStocksLoading(false);
    }
  }, [minConfidence]);

  const fetchIndices = useCallback(async () => {
    try {
      setIndicesLoading(true);
      const res = await phaseAPI.getIndices({ minConfidence });
      setIndices(res.data.indices || []);
    } catch (err) {
      console.error('Error fetching indices:', err);
      setIndices([]);
    } finally {
      setIndicesLoading(false);
    }
  }, [minConfidence]);

  const handleSearch = useCallback(async () => {
    if (!searchTicker.trim()) return;
    const ticker = searchTicker.trim().toUpperCase();
    await openTickerDetail(ticker);
  }, [openTickerDetail, searchTicker]);

  const handlePreMarkupTickerSelect = useCallback((ticker) => {
    setSearchTicker(ticker);
    openTickerDetail(ticker);
  }, [openTickerDetail]);

  const fetchPreMarkup = useCallback(async () => {
    try {
      setPreMarkupLoading(true);
      const res = await phaseAPI.getPreMarkup(0.2, 100);
      setPreMarkup(res.data);
    } catch (err) {
      console.error('Error fetching pre-markup:', err);
    } finally {
      setPreMarkupLoading(false);
    }
  }, []);

  const handleRefresh = useCallback(async () => {
    try {
      setRefreshing(true);
      await phaseAPI.refresh();

      const poll = setInterval(async () => {
        try {
          const res = await phaseAPI.refreshStatus();
          if (res.data.status !== 'running') {
            clearInterval(poll);
            setRefreshing(false);
            fetchSummary();
            fetchStocks(activePhase);
            fetchPreMarkup();
          }
        } catch {
          clearInterval(poll);
          setRefreshing(false);
        }
      }, 2000);
    } catch (err) {
      console.error('Refresh error:', err);
      setRefreshing(false);
    }
  }, [activePhase, fetchPreMarkup, fetchStocks, fetchSummary]);

  useEffect(() => {
    fetchSummary();
    fetchPreMarkup();
    fetchIndices();
  }, [fetchIndices, fetchPreMarkup, fetchSummary]);

  useEffect(() => {
    fetchStocks(activePhase);
  }, [activePhase, fetchStocks]);

  if (loading) {
    return <PhaseSkeleton variant="page" />;
  }

  if (error) {
    return (
      <Box sx={{ p: 4, maxWidth: 1400, mx: 'auto' }}>
        <Alert severity="error" sx={{ borderRadius: '12px' }}>{error}</Alert>
      </Box>
    );
  }

  const stockCount = summary?.counts?.[activePhase]?.count || 0;

  return (
    <Box sx={{ maxWidth: 1400, mx: 'auto', minHeight: '100vh' }}>
      <StockPhasesHeader
        summary={summary}
        tickerQuery={searchTicker}
        onTickerQueryChange={setSearchTicker}
        onSearchSubmit={handleSearch}
        isRefreshing={refreshing}
        onRefresh={handleRefresh}
      />

      <PhaseSummaryCards
        summary={summary}
        activePhase={activePhase}
        onPhaseClick={setActivePhase}
      />

      <PreMarkupSection
        candidates={preMarkup}
        isLoading={preMarkupLoading}
        isExpanded={preMarkupExpanded}
        onToggleExpanded={() => setPreMarkupExpanded(!preMarkupExpanded)}
        onTickerSelect={handlePreMarkupTickerSelect}
      />

      <Box sx={{ mt: 3, mb: 3 }}>
        <Box sx={{ mb: 1.25, px: 0.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Typography sx={{ fontSize: '0.98rem', fontWeight: 800, color: '#0f172a' }}>
            {t('phase.sections.indicesTitle')}
          </Typography>
          <Typography sx={{ fontSize: '0.78rem', fontWeight: 700, color: '#94a3b8' }}>
            {t('phase.sections.indicesCount', { count: indices.length })}
          </Typography>
        </Box>

        {indicesLoading ? (
          <PhaseSkeleton variant="table" rows={3} />
        ) : (
          <PhaseStockTable
            stocks={indices}
            showScores={showScores}
            showCompany={false}
            showSector={false}
            emptyLabel="phase.table.indicesEmpty"
            onRowClick={(item) => openTickerDetail(item.ticker)}
          />
        )}
      </Box>

      <StockPhasesToolbar
        activePhase={activePhase}
        onPhaseChange={setActivePhase}
        summary={summary}
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        showScores={showScores}
        onToggleScores={() => setShowScores(!showScores)}
        visibleCount={stocks.length}
        totalCount={stockCount}
        minConfidence={minConfidence}
        onMinConfidenceChange={setMinConfidence}
        onMinConfidenceCommit={() => fetchStocks(activePhase)}
      />

      <Box>
        {stocksLoading ? (
          <PhaseSkeleton variant="section" rows={6} />
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box>
              <Box sx={{ mb: 1.25, px: 0.25, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ fontSize: '0.82rem', fontWeight: 800, color: '#0f172a' }}>
                  {t('phase.sections.stocksTitle')}
                </Typography>
                <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#94a3b8' }}>
                  {t('phase.sections.stocksCount', { count: stocks.length })}
                </Typography>
              </Box>
              <PhaseStockTable
                stocks={stocks}
                showScores={showScores}
                onRowClick={(item) => openTickerDetail(item.ticker)}
              />
            </Box>
          </Box>
        )}
      </Box>

      <StockPhaseDetailDialog
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        stockDetail={detailData}
        stockHistory={detailHistory}
        isLoading={detailLoading}
        fallbackTicker={searchTicker}
      />
    </Box>
  );
}